import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Eye, Trash2, Tv, Youtube, AlertTriangle, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import CurveChart from "@/components/CurveChart";

const REACH_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

export default function CurveLibrary() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [curves, setCurves] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/curve-sets");
      setCurves(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openDetail = async (cs) => {
    setSelected(cs);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/curve-sets/${cs.id}`);
      setDetail(data);
    } finally {
      setDetailLoading(false);
    }
  };

  const onDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/curve-sets/${confirmDel.id}`);
      toast.success("Curve set deleted");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Curve Library</h2>
          <p className="text-sm text-slate-500 mt-1">All uploaded TV + OLV reach curve sets.</p>
        </div>
        <button
          onClick={() => nav("/upload-curve")}
          className="btn-primary"
          data-testid="curve-library-upload-btn"
        >
          <Plus className="h-4 w-4" /> Upload New Curve
        </button>
      </div>

      <div className="card-harmony overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        ) : curves.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <div className="text-sm">No curve sets yet.</div>
            <button onClick={() => nav("/upload-curve")} className="btn-primary mt-4">
              <Plus className="h-4 w-4" /> Upload your first curve set
            </button>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="curve-library-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-3 px-4">Curve Name</th>
                <th className="text-left py-3 px-4">Target</th>
                <th className="text-left py-3 px-4">Universe</th>
                <th className="text-left py-3 px-4">TV File</th>
                <th className="text-left py-3 px-4">OLV File</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {curves.map((c) => {
                const tv = c.files?.find((f) => f.media_type === "TV");
                const olv = c.files?.find((f) => f.media_type === "OLV");
                return (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{c.curve_name}</div>
                      <div className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</div>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{c.target_audience || "—"}</td>
                    <td className="py-3 px-4 text-slate-600">{fmtNumber(c.universe_size)}</td>
                    <td className="py-3 px-4">
                      {tv ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-700">
                          <Tv className="h-3 w-3 text-[#0A3D91]" /> {tv.original_file_name}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="py-3 px-4">
                      {olv ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-700">
                          <Youtube className="h-3 w-3 text-[#18B7D8]" /> {olv.original_file_name}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                        {c.status}
                      </span>
                      {c.validation_warnings?.length > 0 && (
                        <span className="inline-flex items-center gap-1 ml-2 text-[10px] text-amber-600">
                          <AlertTriangle className="h-3 w-3" /> {c.validation_warnings.length} warning
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => openDetail(c)}
                        className="inline-flex items-center gap-1 text-xs text-[#0E73C9] hover:text-[#0A3D91] font-medium mr-3"
                        data-testid={`curve-view-${c.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                      <button
                        onClick={() => nav(`/optimizer?curve=${c.id}`)}
                        className="inline-flex items-center gap-1 text-xs text-[#0B8F80] hover:text-[#0A3D91] font-medium mr-3"
                      >
                        Use in Optimizer
                      </button>
                      <button
                        onClick={() => setConfirmDel(c)}
                        className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700 font-medium"
                        data-testid={`curve-delete-${c.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{selected?.curve_name}</DialogTitle>
            <DialogDescription>
              {selected?.target_audience} · Universe {fmtNumber(selected?.universe_size)}
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
          ) : detail && (
            <CurveChart detail={detail} />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete curve set?</DialogTitle>
            <DialogDescription>
              This will permanently remove &quot;{confirmDel?.curve_name}&quot; and all its data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setConfirmDel(null)} className="btn-secondary">Cancel</button>
            <button onClick={onDelete} className="btn-primary bg-rose-600 hover:bg-rose-700">Delete</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

